import { type ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { SupportFeatureFlags } from '@common/contexts/support/domain/enums/featureFlags';
import { AuthorizationErrorCode } from '@common/enums';
import { ActionsPermission } from '@common/enums/actionPermission';
import { getOneActionPermission } from '@frontend/services/api/actionPermissionApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useFeatureFlag } from '@frontend/utils/hooks/useFeatureFlag';

export type OutpatientClinicTaskListAuthorizationProps = {
    children: ReactNode;
};

export function OutpatientClinicTaskListAuthorization({ children }: OutpatientClinicTaskListAuthorizationProps) {
    const { areContactsAndTasksEnabled } = useSelector((state: IReduxState) => state.authentication);
    const abortSignal = useAbortSignal();

    const [canRead, setCanRead] = useState(false);

    useEffect(() => {
        canReadCheck();
    }, [areContactsAndTasksEnabled]);

    const isNewOutpatientClinicTaskListEnabled = useFeatureFlag(
        SupportFeatureFlags.EnableOutpatientClinicTaskList,
        false
    );

    if (!isNewOutpatientClinicTaskListEnabled) {
        throw new Error(AuthorizationErrorCode.FEATURE_UNAVAILABLE);
    }

    return canRead ? children : null;

    async function canReadCheck() {
        const response = await getOneActionPermission({
            action: ActionsPermission.PatientTask,
            signal: abortSignal,
        });

        if (!areContactsAndTasksEnabled || !response.canRead) {
            throw new Error(AuthorizationErrorCode.MISSING_OUTPATIENT_CLINIC_TASK_AUTHORIZATION);
        }

        setCanRead(true);
    }
}
